import { useSearchParams } from 'react-router-dom';
import { useRaffles, CATEGORIES } from '../hooks/useRaffles';
import { AREAS } from '../lib/constants';
import RaffleCard, { RaffleCardSkeleton, RaffleCardGrid } from '../components/raffle/RaffleCard';
import EmptyState, { NoRafflesEmpty, NoSearchResultsEmpty } from '../components/shared/EmptyState';
import { Button, Select } from '../components/ui';
import SEO from '../components/shared/SEO';

/**
 * Browse Page - Lucky Number Design System
 *
 * Midnight background, staggered grid with card-enter animations.
 */
export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filter values from URL
  const category = searchParams.get('category') || '';
  const pickupArea = searchParams.get('area') || '';
  const sortBy = searchParams.get('sort') || 'endsAt';

  // Fetch raffles with current filters
  const {
    raffles,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useRaffles({
    category: category || undefined,
    pickupArea: pickupArea || undefined,
    sortBy,
    sortOrder: sortBy === 'endsAt' ? 'asc' : 'desc',
  });

  // Update URL when filters change
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value) {
      searchParams.set('category', value);
    } else {
      searchParams.delete('category');
    }
    setSearchParams(searchParams);
  };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    if (value) {
      searchParams.set('area', value);
    } else {
      searchParams.delete('area');
    }
    setSearchParams(searchParams);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    searchParams.set('sort', value);
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const hasFilters = category || pickupArea || sortBy !== 'endsAt';

  return (
    <div className="min-h-screen bg-midnight">
      <SEO
        title="Browse Competitions"
        description="Browse skill-based competitions for electronics, gaming, furniture, fashion and more. Enter to win amazing items with local pickup across Ireland."
        path="/browse"
      />
      <div className="page-container py-10">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-display text-cream">
            Browse Competitions
          </h1>
          <p className="mt-2 text-cream/60">
            Find amazing items and test your skill
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <div className="flex-1 sm:max-w-xs">
            <Select
              label="Category"
              value={category}
              onChange={handleCategoryChange}
              options={[
                { value: '', label: 'All Categories' },
                ...CATEGORIES,
              ]}
            />
          </div>

          <div className="flex-1 sm:max-w-xs">
            <Select
              label="Location"
              value={pickupArea}
              onChange={handleAreaChange}
              options={[
                { value: '', label: 'All Locations' },
                ...AREAS,
              ]}
            />
          </div>

          <div className="flex-1 sm:max-w-xs">
            <Select
              label="Sort By"
              value={sortBy}
              onChange={handleSortChange}
              options={[
                { value: 'endsAt', label: 'Ending Soon' },
                { value: 'createdAt', label: 'Newest First' },
                { value: 'ticketPrice', label: 'Price: Low to High' },
              ]}
            />
          </div>

          {hasFilters && (
            <div className="flex items-end">
              <Button variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-lg bg-error/10 border border-error/30 p-4 mb-8">
            <p className="text-error">
              Error loading competitions. Please try again.
            </p>
            <Button variant="outline" size="sm" onClick={refresh} className="mt-2">
              Retry
            </Button>
          </div>
        )}

        {/* Loading state */}
        {loading && raffles.length === 0 && (
          <RaffleCardGrid>
            {[...Array(8)].map((_, i) => (
              <RaffleCardSkeleton key={i} />
            ))}
          </RaffleCardGrid>
        )}

        {/* Empty state */}
        {!loading && raffles.length === 0 && !error && (
          (category || pickupArea) ? (
            <NoSearchResultsEmpty
              searchTerm={[
                CATEGORIES.find(c => c.value === category)?.label,
                AREAS.find(a => a.value === pickupArea)?.label,
              ].filter(Boolean).join(' in ')}
              onClearSearch={clearFilters}
            />
          ) : (
            <NoRafflesEmpty onCreateRaffle={() => window.location.href = '/create'} />
          )
        )}

        {/* Raffle grid */}
        {raffles.length > 0 && (
          <>
            <RaffleCardGrid>
              {raffles.map((raffle) => (
                <RaffleCard key={raffle.id} raffle={raffle} />
              ))}
            </RaffleCardGrid>

            {/* Load more */}
            {hasMore && (
              <div className="mt-12 text-center">
                <Button
                  variant="secondary"
                  onClick={loadMore}
                  loading={loading}
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
